import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import RestrictionBadge from "../components/RestrictionBadge";
import { Star } from "lucide-react";

const STATUS_STYLES = {
  approved: { bg: "#E1F2E3", text: "#276832", label: "Aprovado" },
  pending: { bg: "#FCE9D2", text: "#A35C14", label: "Aguardando" },
  rejected: { bg: "#FCD8D8", text: "#8B1A1A", label: "Rejeitado" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return <span style={{ background: s.bg, color: s.text }} className="px-3 py-1 rounded-full text-xs font-bold">{s.label}</span>;
}

export default function Profile() {
  const { user } = useAuth();
  const [places, setPlaces] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/me/places").then((r) => setPlaces(r.data));
    api.get("/me/reviews").then((r) => setReviews(r.data));
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-20 text-center">
        <p>Faça <Link to="/login" className="text-brand-primary font-bold underline">login</Link> para ver seu perfil.</p>
      </div>
    );
  }

  return (
    <div data-testid="profile-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center gap-5 mb-12">
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full object-cover" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center font-heading font-black text-3xl">
            {(user.name || user.email)[0].toUpperCase()}
          </div>
        )}
        <div>
          <h1 className="font-heading font-black text-3xl text-brand-text">{user.name || user.email}</h1>
          <p className="text-brand-textMuted">{user.email}</p>
          <span className="inline-block mt-2 text-xs font-bold uppercase tracking-wider text-brand-secondary">{user.role}</span>
        </div>
      </div>

      <section className="mb-12">
        <h2 className="font-heading font-bold text-2xl text-brand-text mb-6">Meus lugares cadastrados</h2>
        {places.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-brand-border p-10 text-center text-brand-textMuted">
            Você ainda não cadastrou lugares. <Link to="/cadastrar-lugar" className="text-brand-primary font-bold">Cadastrar agora →</Link>
          </div>
        ) : (
          <div className="grid gap-3">
            {places.map((p) => (
              <Link to={`/lugar/${p.place_id}`} key={p.place_id} className="rounded-2xl bg-white border border-brand-border p-5 flex flex-wrap items-center justify-between gap-3 hover:border-brand-primary transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-1.5 mb-1">{(p.restrictions || []).map((r) => <RestrictionBadge key={r} type={r} />)}</div>
                  <h3 className="font-bold text-brand-text truncate">{p.name}</h3>
                  <p className="text-sm text-brand-textMuted truncate">{p.city} · {p.state}</p>
                </div>
                <StatusPill status={p.status} />
              </Link>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-heading font-bold text-2xl text-brand-text mb-6">Minhas avaliações</h2>
        {reviews.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-brand-border p-10 text-center text-brand-textMuted">Nenhuma avaliação ainda.</div>
        ) : (
          <div className="grid gap-3">
            {reviews.map((r) => (
              <div key={r.review_id} className="rounded-2xl bg-white border border-brand-border p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-bold text-brand-text">{r.place_name}</p>
                    <div className="flex items-center gap-0.5 mt-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={`star-${n}`} className={`w-4 h-4 ${n <= r.rating ? "fill-brand-yellow text-brand-yellow" : "text-brand-border"}`} />
                      ))}
                    </div>
                  </div>
                  <StatusPill status={r.status} />
                </div>
                <p className="text-brand-text/90 mt-3 leading-relaxed">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
